import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { INK_BLACK } from "../../theme/tokens";
import type { GapPostedCoverage } from "./utils";

type Props = {
	onClick: () => void;
	enabled?: boolean;
	/** Aggregate posted-shift coverage for this gap's signature (pickup_by_location OFF only). */
	coverage: GapPostedCoverage | null;
	/** Scroll to + pulse the posted shift identified by `coverage.targetRequestId`. */
	onCoverageClick: (requestId: string) => void;
};

export const ClickableGap: FC<Props> = ({ onClick, enabled, coverage, onCoverageClick }) => {
	const { t } = useTranslation();

	// Show the coverage label only when a posted shift actually covers part of this signature (X > 0) and
	// there is a concrete post to scroll to. X === 0 renders a plain "Missing +".
	const showCoverage = coverage != null && coverage.openCapacity > 0 && coverage.targetRequestId != null;
	const coverageLabel = !showCoverage
		? null
		: coverage.openCapacity >= coverage.missingCount
			? t("day_view_gaps.gap.fully_posted")
			: t("day_view_gaps.gap.partially_posted", { posted: coverage.openCapacity, missing: coverage.missingCount });

	const handleCoverageClick = (e: MouseEvent<HTMLElement>) => {
		// Stop propagation so clicking the label scrolls to the posted shift instead of opening the dialog.
		e.stopPropagation();
		if (coverage?.targetRequestId) onCoverageClick?.(coverage.targetRequestId);
	};

	return (
		<Box
			onClick={enabled ? onClick : undefined}
			sx={{
				p: 0.5,
				pl: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "start",
				gap: 0.5,
				":hover": {
					cursor: enabled ? "pointer" : "default",
					background: enabled ? alpha(INK_BLACK, 0.1) : "transparent",
					transition: "background-color 0.25s ease",
					borderRadius: 1,
				},
			}}
		>
			{t("day_view_gaps.gap.missing")}
			{coverageLabel != null && (
				<Box
					component="span"
					onClick={handleCoverageClick}
					sx={{
						color: "inherit",
						cursor: "pointer",
						// Hover-underline only on real pointers so touch devices don't get a stuck underline.
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": { textDecoration: "underline" },
						},
					}}
				>
					{coverageLabel}
				</Box>
			)}
		</Box>
	);
};
